import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function ApplicationForm() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    furigana: '',
    email: '',
    phone: '',
    preferredDate: '',
    interest: '',
    question: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = t('seminar.form.error.name');
    if (!formData.furigana.trim()) newErrors.furigana = t('seminar.form.error.furigana');
    if (!formData.email.trim()) {
      newErrors.email = t('seminar.form.error.email.required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('seminar.form.error.email.invalid');
    }
    if (!formData.phone.trim()) {
      newErrors.phone = t('seminar.form.error.phone');
    }
    if (!formData.interest) {
      newErrors.interest = t('seminar.form.error.interest');
    }
    if (formData.question.length > 500) {
      newErrors.question = t('seminar.form.error.question');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      navigate('/seminar/confirm', { state: { formData } });
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  return (
    <section className="relative py-20 md:py-28 bg-background-50" id="apply-form">
      <div className="max-w-[700px] mx-auto px-8">
        <div className="text-center mb-14">
          {String(t('seminar.form.label')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-accent-600 text-[13px] tracking-[0.2em] mb-5 font-label">{__s}</p>))}
          <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-4">
            {t('seminar.form.title')}
          </h2>
          <div className="gold-line mx-auto mb-4"></div>
          {String(t('seminar.form.lead')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[16px] text-foreground-600 tracking-[0.04em] leading-relaxed">{__s}</p>))}
        </div>

        {/* Phone / Email contact */}
        <div className="text-center mb-10">
          <p className="text-[14px] text-foreground-500 tracking-[0.04em] leading-relaxed">
            {t('seminar.form.contact.intro')}{' '}
            <a href="tel:0368244086" className="text-foreground-700 font-medium hover:text-accent-600 transition-colors">
              03-6824-4086
            </a>{' '}
            {t('seminar.form.contact.or')}{' '}
            <a href="mailto:info@butler-academy.jp" className="text-accent-600 hover:text-accent-500 transition-colors">
              info@butler-academy.jp
            </a>{' '}
            {t('seminar.form.contact.also')}
          </p>
          {String(t('seminar.form.contact.hours')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[13px] text-foreground-400 mt-1.5 tracking-[0.03em]">{__s}</p>))}
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className="space-y-6">
            {/* Full Name */}
            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('seminar.form.field.name')} <span className="text-accent-600 text-[12px]">{t('seminar.form.required')}</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 placeholder:text-foreground-400"
                placeholder={t('seminar.form.placeholder.name')}
              />
              {errors.name && (
                <p className="text-[13px] text-red-500 mt-1.5 tracking-[0.03em]">{errors.name}</p>
              )}
            </div>

            {/* Furigana */}
            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('seminar.form.field.furigana')} <span className="text-accent-600 text-[12px]">{t('seminar.form.required')}</span>
              </label>
              <input
                type="text"
                name="furigana"
                value={formData.furigana}
                onChange={(e) => handleChange('furigana', e.target.value)}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 placeholder:text-foreground-400"
                placeholder={t('seminar.form.placeholder.furigana')}
              />
              {errors.furigana && (
                <p className="text-[13px] text-red-500 mt-1.5 tracking-[0.03em]">{errors.furigana}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('seminar.form.field.email')} <span className="text-accent-600 text-[12px]">{t('seminar.form.required')}</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 placeholder:text-foreground-400"
                placeholder={t('seminar.form.placeholder.email')}
              />
              {errors.email && (
                <p className="text-[13px] text-red-500 mt-1.5 tracking-[0.03em]">{errors.email}</p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('seminar.form.field.phone')} <span className="text-accent-600 text-[12px]">{t('seminar.form.required')}</span>
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 placeholder:text-foreground-400"
                placeholder={t('seminar.form.placeholder.phone')}
              />
              {errors.phone && (
                <p className="text-[13px] text-red-500 mt-1.5 tracking-[0.03em]">{errors.phone}</p>
              )}
            </div>

            {/* Preferred Date */}
            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('seminar.form.field.date')}
              </label>
              <input
                type="text"
                name="preferredDate"
                value={formData.preferredDate}
                onChange={(e) => handleChange('preferredDate', e.target.value)}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 placeholder:text-foreground-400"
                placeholder={t('seminar.form.placeholder.date')}
              />
            </div>

            {/* Interest in Full Programme */}
            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('seminar.form.field.interest')} <span className="text-accent-600 text-[12px]">{t('seminar.form.required')}</span>
              </label>
              <select
                name="interest"
                value={formData.interest}
                onChange={(e) => handleChange('interest', e.target.value)}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 cursor-pointer appearance-none"
              >
                <option value="" disabled>{t('seminar.form.placeholder.interest')}</option>
                <option value="considering">{t('seminar.form.interest.considering')}</option>
                <option value="researching">{t('seminar.form.interest.researching')}</option>
                <option value="undecided">{t('seminar.form.interest.undecided')}</option>
              </select>
              {errors.interest && (
                <p className="text-[13px] text-red-500 mt-1.5 tracking-[0.03em]">{errors.interest}</p>
              )}
            </div>

            {/* Questions */}
            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('seminar.form.field.question')}
              </label>
              <textarea
                name="question"
                value={formData.question}
                onChange={(e) => handleChange('question', e.target.value)}
                rows={4}
                maxLength={500}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 placeholder:text-foreground-400 resize-none"
                placeholder={t('seminar.form.placeholder.question')}
              ></textarea>
              <p className="text-[13px] text-foreground-400 mt-1 text-right">
                {formData.question.length}/500
              </p>
              {errors.question && (
                <p className="text-[13px] text-red-500 mt-1.5 tracking-[0.03em]">{errors.question}</p>
              )}
            </div>

            {/* Submit */}
            <div className="pt-4">
              <button
                type="submit"
                className="btn-glow w-full py-3.5 bg-accent-500/90 hover:bg-accent-500 text-primary-900 text-[15px] tracking-[0.1em] font-medium transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                {t('seminar.form.submit')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </section>
  );
}
